import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Space,
  Card,
  Divider,
  Radio,
  message,
  Table,
  Modal,
  Collapse,
  Typography,
  Tooltip,
  Checkbox,
  Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, ThunderboltOutlined, MedicineBoxOutlined, SearchOutlined } from '@ant-design/icons';
import { WorkOrderLineItem, WorkOrderLinePart, LineItemTemplate, PartSavedTemplate } from '../../types';
import api, { shopSettingsApi, lineItemTemplateApi, partSavedTemplateApi } from '../../services/api';
import { useHasRole } from '../auth/RoleGuard';
import { useAuth } from '../../contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;
const { Text } = Typography;

interface WorkOrderLineItemFormProps {
  workOrderId: number;
  lineItem?: WorkOrderLineItem;
  existingLineItems?: WorkOrderLineItem[];
  onSave: () => void;
  onCancel: () => void;
  aiSuggestedCCC?: { concern?: string; cause?: string; correction?: string };
  vehicle?: { year: number; make: string; model: string; vin?: string; engine_size?: string };
}

const WorkOrderLineItemForm: React.FC<WorkOrderLineItemFormProps> = ({
  workOrderId,
  lineItem,
  existingLineItems = [],
  onSave,
  onCancel,
  aiSuggestedCCC,
  vehicle,
}) => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canSeeCost = useHasRole(['owner', 'advisor']);
  const [loading, setLoading] = useState(false);
  const [parts, setParts] = useState<WorkOrderLinePart[]>([]);
  const [partModalVisible, setPartModalVisible] = useState(false);
  const [partForm] = Form.useForm();
  const [editingPartIndex, setEditingPartIndex] = useState<number | null>(null);
  const [defaultLaborRate, setDefaultLaborRate] = useState<number>(0);
  const [partTotal, setPartTotal] = useState<number>(0);
  const [lineItemTemplates, setLineItemTemplates] = useState<LineItemTemplate[]>([]);
  const [partTemplates, setPartTemplates] = useState<PartSavedTemplate[]>([]);
  const [isDiagnosis, setIsDiagnosis] = useState(false);
  const [woPartsMarkup, setWoPartsMarkup] = useState(40);
  const [showPriceChangeModal, setShowPriceChangeModal] = useState(false);
  const [priceChangeData, setPriceChangeData] = useState<any>(null);
  const [priceChangeChoice, setPriceChangeChoice] = useState<'resend' | 'in_person' | 'proceed'>('resend');

  useEffect(() => {
    // Load shop settings to get default labor rate
    const loadShopSettings = async () => {
      try {
        const response = await shopSettingsApi.get();
        const laborRate = response.data.labor_rate || 0;
        setDefaultLaborRate(laborRate);

        // If this is a new line item (not editing), set the default labor rate
        if (!lineItem) {
          form.setFieldsValue({ labor_rate: laborRate });
        }
      } catch (error) {
        console.error('Error loading shop settings:', error);
      }
    };

    // Load templates
    const loadTemplates = async () => {
      try {
        const [lineItemRes, partRes] = await Promise.all([
          lineItemTemplateApi.getAll(),
          partSavedTemplateApi.getAll(),
        ]);
        setLineItemTemplates(lineItemRes.data);
        setPartTemplates(partRes.data);
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };

    loadShopSettings();
    loadTemplates();
  }, []);

  useEffect(() => {
    if (lineItem) {
      form.setFieldsValue({
        ...lineItem,
        labor_hours: lineItem.labor_hours || 0,
        labor_rate: lineItem.labor_rate || defaultLaborRate,
        labor_discount_percentage: lineItem.labor_discount_percentage || 0,
        labor_discount_amount: lineItem.labor_discount_amount || 0,
        is_diagnosis: lineItem.is_diagnosis || false,
        linked_diagnosis_id: lineItem.linked_diagnosis_id,
      });
      setParts(lineItem.parts || []);
      setIsDiagnosis(lineItem.is_diagnosis || false);
    }
  }, [lineItem, form, defaultLaborRate]);

  // Populate form with AI-suggested 3 C's values when the prop is provided
  useEffect(() => {
    if (aiSuggestedCCC && !lineItem) {
      const patch: Record<string, string> = {};
      if (aiSuggestedCCC.concern) patch.concern = aiSuggestedCCC.concern;
      if (aiSuggestedCCC.cause) patch.cause = aiSuggestedCCC.cause;
      if (aiSuggestedCCC.correction) patch.correction = aiSuggestedCCC.correction;
      if (Object.keys(patch).length > 0) {
        form.setFieldsValue(patch);
      }
    }
  }, [aiSuggestedCCC, form, lineItem]);

  const handleAddPart = () => {
    setEditingPartIndex(null);
    partForm.resetFields();
    setPartTotal(0);
    setPartModalVisible(true);
  };

  const handleEditPart = (index: number) => {
    setEditingPartIndex(index);
    const part = parts[index];
    partForm.setFieldsValue({
      ...part,
      expected_delivery: part.expected_delivery ? part.expected_delivery.split('T')[0] : '',
    });
    setPartTotal(part.total_price || 0);
    setPartModalVisible(true);
  };

  const handleLaborChange = () => {
    const laborHours = form.getFieldValue('labor_hours') || 0;
    const laborRate = form.getFieldValue('labor_rate') || 0;
    const discountPercentage = form.getFieldValue('labor_discount_percentage') || 0;

    const laborSubtotal = laborHours * laborRate;
    const discountAmount = (laborSubtotal * discountPercentage) / 100;

    form.setFieldsValue({
      labor_discount_amount: discountAmount,
    });
  };

  const handlePartFormChange = () => {
    const quantity = partForm.getFieldValue('quantity') || 0;
    const unitPrice = partForm.getFieldValue('unit_price') || 0;
    const discountPercentage = partForm.getFieldValue('discount_percentage') || 0;

    const subtotal = quantity * unitPrice;
    const discountAmount = (subtotal * discountPercentage) / 100;
    const discountedPrice = unitPrice - (discountAmount / quantity);
    const totalPrice = subtotal - discountAmount;

    partForm.setFieldsValue({
      discount_amount: discountAmount,
      discounted_price: discountedPrice,
    });

    setPartTotal(totalPrice);
  };

  const handleLineItemTemplateSelect = (templateId: number) => {
    const template = lineItemTemplates.find(t => t.id === templateId);
    if (template) {
      form.setFieldsValue({
        concern: template.description,
        labor_hours: template.labor_hours || 0,
        labor_rate: template.hourly_rate || defaultLaborRate,
        labor_discount_percentage: template.labor_discount_percentage || 0,
        labor_discount_amount: template.labor_discount_amount || 0,
        notes: template.notes || '',
      });
      handleLaborChange();
      message.success(`Loaded template: ${template.name}`);
    }
  };

  const handlePartTemplateSelect = (templateId: number) => {
    const template = partTemplates.find(t => t.id === templateId);
    if (template) {
      partForm.setFieldsValue({
        part_name: template.name,
        part_number: template.part_number,
        unit_price: template.unit_price,
        discount_percentage: template.discount_percentage || 0,
        discount_amount: template.discount_amount || 0,
        supplier: template.supplier || '',
        notes: template.notes || '',
        quantity: 1,
        status: 'needed',
      });
      handlePartFormChange();
      message.success(`Loaded template: ${template.name}`);
    }
  };

  const handleDeletePart = (index: number) => {
    const newParts = parts.filter((_, i) => i !== index);
    setParts(newParts);
    message.success('Part removed');
  };

  const handlePartModalOk = async () => {
    try {
      const values = await partForm.validateFields();
      const subtotal = values.quantity * values.unit_price;
      const discountAmount = values.discount_amount || 0;
      const totalPrice = subtotal - discountAmount;

      const newPart: WorkOrderLinePart = {
        ...values,
        total_price: totalPrice,
        discount_percentage: values.discount_percentage || 0,
        discount_amount: discountAmount,
        discounted_price: values.discounted_price,
        line_item_id: lineItem?.id || 0,
        cost: values.cost || null,
      };

      if (editingPartIndex !== null) {
        const newParts = [...parts];
        newParts[editingPartIndex] = newPart;
        setParts(newParts);
        message.success('Part updated');
      } else {
        setParts([...parts, newPart]);
        message.success('Part added');
      }

      setPartModalVisible(false);
      partForm.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const calculateTotals = (values: any) => {
    const laborSubtotal = (values.labor_hours || 0) * (values.labor_rate || 0);
    const laborDiscountAmount = values.labor_discount_amount || 0;
    const laborTotal = Math.max(0, laborSubtotal - laborDiscountAmount);
    const partsTotal = parts.reduce((sum, part) => sum + part.total_price, 0);
    const lineTotal = laborTotal + partsTotal;

    return { laborTotal, partsTotal, lineTotal };
  };

  const buildPayload = (values: any) => {
    const totals = calculateTotals(values);

    // Clean up parts - remove database fields and convert null to undefined
    const cleanParts = parts.map(part => {
      const cleanPart: any = {
        part_name: part.part_name,
        quantity: part.quantity,
        unit_price: part.unit_price,
        total_price: part.total_price,
        status: part.status || 'needed',
        discount_percentage: part.discount_percentage || 0,
        discount_amount: part.discount_amount || 0,
        cost: part.cost || null,
      };

      // Only include optional fields if they have truthy values
      if (part.part_number) cleanPart.part_number = part.part_number;
      if (part.supplier) cleanPart.supplier = part.supplier;
      if (part.tracking_number) cleanPart.tracking_number = part.tracking_number;
      if (part.expected_delivery) cleanPart.expected_delivery = part.expected_delivery;
      if (part.notes) cleanPart.notes = part.notes;
      if (part.discounted_price != null) cleanPart.discounted_price = part.discounted_price;

      return cleanPart;
    });

    // Calculate line number for new items
    let lineNumber = lineItem?.line_number;
    if (!lineNumber) {
      const maxLineNumber = existingLineItems.reduce((max, item) =>
        Math.max(max, item.line_number || 0), 0);
      lineNumber = maxLineNumber + 1;
    }

    // Build payload with only defined values
    const payload: any = {
      concern: values.concern,
      parts: cleanParts,
      labor_total: totals.laborTotal,
      parts_total: totals.partsTotal,
      line_total: totals.lineTotal,
      line_number: lineNumber,
      shop_status: values.shop_status || 'new',
      customer_approval_status: values.customer_approval_status || 'pending',
      labor_hours: values.labor_hours || 0,
      labor_rate: values.labor_rate || 0,
      labor_discount_percentage: values.labor_discount_percentage || 0,
      labor_discount_amount: values.labor_discount_amount || 0,
      is_diagnosis: values.is_diagnosis || false,
    };

    // Add optional fields only if they have values
    if (values.cause) payload.cause = values.cause;
    if (values.correction) payload.correction = values.correction;
    if (values.notes) payload.notes = values.notes;
    if (values.customer_notes) payload.customer_notes = values.customer_notes;
    if (values.linked_diagnosis_id) payload.linked_diagnosis_id = values.linked_diagnosis_id;

    return payload;
  };

  const saveLineItem = async (payload: any) => {
    if (lineItem?.id) {
      await api.put(`/work-orders/${workOrderId}/line-items/${lineItem.id}`, payload);
    } else {
      payload.work_order_id = workOrderId;
      await api.post(`/work-orders/${workOrderId}/line-items`, payload);
    }
    message.success(lineItem?.id ? 'Line item updated' : 'Line item created');
    onSave();
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const payload = buildPayload(values);

      // Check if this is an approved item with a price change
      if (lineItem?.id && lineItem.customer_approval_status === 'approved') {
        const oldTotal = lineItem.line_total || 0;
        const newTotal = payload.line_total || (payload.labor_total || 0) + (payload.parts?.reduce((s: number, p: any) => s + (p.total_price || 0), 0) || 0);
        if (Math.abs(oldTotal - newTotal) > 0.01) {
          setPriceChangeData({ oldTotal, newTotal, payload });
          setShowPriceChangeModal(true);
          setLoading(false);
          return;
        }
      }

      await saveLineItem(payload);
    } catch (error: any) {
      message.error('Failed to save line item');
      console.error('Error saving line item:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.data?.details) {
        console.error('Validation details:', JSON.stringify(error.response.data.details, null, 2));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChangeConfirm = async () => {
    try {
      setLoading(true);
      await saveLineItem(priceChangeData.payload);

      if (priceChangeChoice === 'resend') {
        await api.patch(`/work-orders/${workOrderId}/line-items/${lineItem!.id}/customer-status`, {
          status: 'pending',
        });
        message.info('Approval status reset to pending');
      } else if (priceChangeChoice === 'in_person') {
        const staffName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'Staff';
        await api.post(`/work-orders/${workOrderId}/line-items/${lineItem!.id}/approve-in-person`, {
          staff_name: staffName,
          notes: `Re-approved in-person after price change: $${priceChangeData.oldTotal.toFixed(2)} → $${priceChangeData.newTotal.toFixed(2)}`,
          confirmed: true,
        });
        message.success('Re-approved in-person');
      } else {
        message.warning('Price changed without customer re-approval');
      }

      setShowPriceChangeModal(false);
      onSave();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to process');
    } finally {
      setLoading(false);
    }
  };

  const partColumns = [
    {
      title: 'Part Name',
      dataIndex: 'part_name',
      key: 'part_name',
      ellipsis: true,
    },
    {
      title: 'Part #',
      dataIndex: 'part_number',
      key: 'part_number',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
    },
    {
      title: 'Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 80,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Discount',
      key: 'discount',
      width: 80,
      render: (_: any, record: WorkOrderLinePart) => {
        if (record.discount_percentage && record.discount_percentage > 0) {
          return <span style={{ color: '#52c41a' }}>{record.discount_percentage}%</span>;
        }
        return '-';
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 80,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: any, __: any, index: number) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleEditPart(index)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            onClick={() => handleDeletePart(index)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const isMobileView = window.innerWidth < 768;

  return (
    <Form
      form={form}
      layout="vertical"
      id="line-item-form"
      onFinish={handleSubmit}
      onValuesChange={(changedValues) => {
        // Only recalculate labor totals when numeric labor fields change
        const laborFields = ['labor_hours', 'labor_rate', 'labor_discount_percentage', 'labor_discount_amount'];
        const changedKeys = Object.keys(changedValues);
        if (changedKeys.some(k => laborFields.includes(k))) {
          handleLaborChange();
        }
        if ('is_diagnosis' in changedValues) {
          setIsDiagnosis(changedValues.is_diagnosis);
          // Clear linked_diagnosis_id if marking as diagnosis
          if (changedValues.is_diagnosis) {
            form.setFieldsValue({ linked_diagnosis_id: undefined });
          }
        }
      }}
      initialValues={{
        shop_status: 'new',
        customer_approval_status: 'pending',
        labor_hours: 0,
        labor_rate: defaultLaborRate,
        labor_discount_percentage: 0,
        labor_discount_amount: 0,
        is_diagnosis: false,
      }}
    >
      {/* Template Selector */}
      {!lineItem && lineItemTemplates.length > 0 && (
        <Card size="small" className="mb-4" style={{ backgroundColor: '#f0f9ff', borderColor: '#1890ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ThunderboltOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#1890ff' }}>Quick Start from Template</div>
              <Select
                placeholder="Select a template to auto-fill this line item..."
                allowClear
                showSearch
                style={{ width: '100%' }}
                onChange={handleLineItemTemplateSelect}
                filterOption={(input, option) =>
                  String((option?.children as unknown) as string).toLowerCase().includes(input.toLowerCase())
                }
              >
                {lineItemTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name} - {template.category}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      )}


      {aiSuggestedCCC &&
        (aiSuggestedCCC.concern || aiSuggestedCCC.cause || aiSuggestedCCC.correction) && (
          <Alert
            type="info"
            showIcon
            message="AI Suggested — Review and edit as needed"
            style={{ marginBottom: 8, fontSize: 12 }}
          />
        )}

      <Card title="The 3 C's" size="small" className="mb-4">
        <Form.Item
          name="concern"
          label="Concern (What the customer reported)"
          rules={[{ required: true, message: 'Please enter the customer concern' }]}
        >
          <TextArea
            rows={3}
            placeholder="Describe what the customer reported..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="cause"
          label="Cause (What we found was wrong)"
        >
          <TextArea
            rows={3}
            placeholder="Describe what we diagnosed..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="correction"
          label="Correction (What we did to fix it)"
        >
          <TextArea
            rows={3}
            placeholder="Describe what we did to correct the issue..."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Card>

      <Card title="Status" size="small" className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Item
            name="shop_status"
            label="Shop Status"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="new">New</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="waiting_parts">Waiting Parts</Option>
              <Option value="need_approval">Need Approval</Option>
              <Option value="completed">Completed</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="customer_approval_status"
            label="Customer Approval"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="pending">Pending - Not yet approved</Option>
              <Option value="approved">Approved - OK to work (verbal/written)</Option>
              <Option value="declined">Declined - Customer said no</Option>
            </Select>
          </Form.Item>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Form.Item
          name="is_diagnosis"
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox>
            <Space>
              <MedicineBoxOutlined style={{ color: '#f59e0b' }} />
              <span style={{ fontWeight: 600 }}>Mark as Diagnosis Line Item</span>
            </Space>
          </Checkbox>
        </Form.Item>

        <Alert
          message="Diagnosis line items can be waived (made free) if the customer approves a repair. The diagnosis fee will be applied as a credit to the approved repair(s)."
          type="info"
          showIcon
          style={{ marginTop: 12, fontSize: 12 }}
        />

        {/* Show linked diagnosis dropdown only for repair items (not diagnosis items) */}
        {!isDiagnosis && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Form.Item
              name="linked_diagnosis_id"
              label={
                <span>
                  <MedicineBoxOutlined style={{ color: '#10b981', marginRight: 8 }} />
                  <strong>Link to Diagnosis (Optional)</strong>
                </span>
              }
              style={{ marginBottom: 0 }}
            >
              <Select
                placeholder="Select diagnosis to auto-waive when this repair is approved..."
                allowClear
                disabled={existingLineItems.filter(item => item.is_diagnosis).length === 0}
              >
                {existingLineItems
                  .filter(item => item.is_diagnosis && item.id !== lineItem?.id)
                  .map(diagnosisItem => (
                    <Option key={diagnosisItem.id} value={diagnosisItem.id}>
                      {diagnosisItem.concern} - ${(diagnosisItem.line_total || 0).toFixed(2)}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Alert
              message="When customer approves this repair via public link, the linked diagnosis will automatically be waived (made free)."
              type="success"
              showIcon
              style={{ marginTop: 12, fontSize: 12 }}
            />
          </>
        )}
      </Card>

      <Card
        title="Labor"
        size="small"
        className="mb-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Item
            name="labor_hours"
            label="Labor Hours"
          >
            <InputNumber
              min={0}
              step={0.5}
              precision={2}
              className="w-full"
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="labor_rate"
            label="Labor Rate ($/hr)"
          >
            <InputNumber
              min={0}
              step={5}
              precision={2}
              className="w-full"
              placeholder="0.00"
              prefix="$"
            />
          </Form.Item>

          <Form.Item
            name="labor_discount_percentage"
            label="Discount (%)"
          >
            <InputNumber
              min={0}
              max={100}
              step={5}
              precision={2}
              className="w-full"
              placeholder="0.00"
              suffix="%"
            />
          </Form.Item>

          <Form.Item
            name="labor_discount_amount"
            label="Discount Amount"
          >
            <InputNumber
              min={0}
              precision={2}
              className="w-full"
              placeholder="0.00"
              prefix="$"
              disabled
            />
          </Form.Item>
        </div>
        {(() => {
          const hours = form.getFieldValue('labor_hours') || 0;
          const rate = form.getFieldValue('labor_rate') || 0;
          const discPct = form.getFieldValue('labor_discount_percentage') || 0;
          const gross = hours * rate;
          const discAmt = Math.round(gross * discPct) / 100;
          const net = gross - discAmt;
          if (gross > 0) {
            return (
              <div style={{ padding: '4px 8px', backgroundColor: '#f6ffed', borderRadius: 4, fontSize: 12, marginBottom: 12 }}>
                {hours} hrs × ${rate.toFixed(2)}/hr = ${gross.toFixed(2)}
                {discPct > 0 && <> − {discPct}% (${discAmt.toFixed(2)}) = <strong>${net.toFixed(2)}</strong></>}
              </div>
            );
          }
          return null;
        })()}
      </Card>

      <Card
        title={
          <div className="flex justify-between items-center">
            <span>Parts</span>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddPart}
            >
              Add Part
            </Button>
          </div>
        }
        size="small"
        className="mb-4"
      >
        {parts.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No parts added yet
          </div>
        ) : window.innerWidth < 768 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {parts.map((part, index) => (
              <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{part.part_name}</div>
                    {part.part_number && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>PN: {part.part_number}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
                      <span>Qty: {part.quantity}</span>
                      <span>${part.unit_price.toFixed(2)} ea</span>
                      {part.discount_percentage && part.discount_percentage > 0 && (
                        <span style={{ color: '#52c41a' }}>-{part.discount_percentage}%</span>
                      )}
                      <span>Status: {part.status}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: '#22c55e', fontSize: 14 }}>${part.total_price.toFixed(2)}</div>
                    <Space size="small" style={{ marginTop: 4 }}>
                      <Button type="link" size="small" onClick={() => handleEditPart(index)}>Edit</Button>
                      <Button type="link" size="small" danger onClick={() => handleDeletePart(index)}>Delete</Button>
                    </Space>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            dataSource={parts}
            columns={partColumns}
            pagination={false}
            size="small"
            rowKey={(record) => String(record.id ?? record.part_number ?? record.part_name ?? `part-${record.quantity}`)}
            scroll={{ x: 700 }}
          />
        )}
      </Card>

      <Collapse size="small" className="mb-4">
        <Panel header="Additional Notes" key="1">
          <Form.Item
            name="notes"
            label="Internal Notes (Not visible to customer)"
          >
            <TextArea
              rows={2}
              placeholder="Internal notes..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="customer_notes"
            label="Customer Notes (Visible to customer)"
          >
            <TextArea
              rows={2}
              placeholder="Notes for customer..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Panel>
      </Collapse>

      {/* Buttons are rendered in the Drawer footer via form id="line-item-form" */}

      {/* Part Modal */}
      <Modal
        title={editingPartIndex !== null ? 'Edit Part' : 'Add Part'}
        open={partModalVisible}
        onOk={handlePartModalOk}
        onCancel={() => {
          setPartModalVisible(false);
          partForm.resetFields();
        }}
        okText="Save"
        width={isMobileView ? '100%' : 600}
        style={isMobileView ? { top: 20, maxWidth: '100vw', margin: '0 auto' } : undefined}
      >
        <Form
          form={partForm}
          layout="vertical"
          initialValues={{
            quantity: 1,
            unit_price: 0,
            status: 'needed',
            discount_percentage: 0,
            discount_amount: 0,
          }}
          onValuesChange={handlePartFormChange}
        >
          {/* Part Template Selector */}
          {editingPartIndex === null && partTemplates.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f9ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <ThunderboltOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                <Text strong style={{ color: '#1890ff' }}>Load from Template</Text>
              </div>
              <Select
                placeholder="Select a part template..."
                allowClear
                showSearch
                style={{ width: '100%' }}
                onChange={handlePartTemplateSelect}
                filterOption={(input, option) =>
                  String((option?.children as unknown) as string).toLowerCase().includes(input.toLowerCase())
                }
              >
                {partTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name} - {template.part_number}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          <Form.Item
            name="part_name"
            label="Part Name"
            rules={[{ required: true, message: 'Please enter part name' }]}
            style={{ marginBottom: 16 }}
          >
            <Input placeholder="Part name..." />
          </Form.Item>

          <Form.Item
            name="part_number"
            label="Part Number"
            style={{ marginBottom: 16 }}
          >
            <Input placeholder="Part number..." />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4" style={{ marginBottom: 16 }}>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[{ required: true, message: 'Required' }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber min={1} className="w-full" />
            </Form.Item>

            <Form.Item
              name="unit_price"
              label="Unit Price"
              rules={[{ required: true, message: 'Required' }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                precision={2}
                className="w-full"
                prefix="$"
              />
            </Form.Item>
          </div>

          {canSeeCost && (
            <Form.Item name="cost" label="Shop Cost (internal)" style={{ marginBottom: 16 }}>
              <InputNumber
                prefix="$"
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="Optional — what we pay"
              />
            </Form.Item>
          )}

          {canSeeCost && (() => {
            const costVal = partForm.getFieldValue('cost');
            const priceVal = partForm.getFieldValue('unit_price');
            if (costVal != null && costVal > 0 && priceVal != null && priceVal > 0) {
              const margin = ((priceVal - costVal) / priceVal) * 100;
              return (
                <div style={{
                  padding: '4px 8px',
                  borderRadius: 4,
                  backgroundColor: margin >= 0 ? '#f6ffed' : '#fff2f0',
                  color: margin >= 0 ? '#52c41a' : '#ff4d4f',
                  fontSize: 12,
                  marginBottom: 12,
                }}>
                  Margin: ${(priceVal - costVal).toFixed(2)} ({margin.toFixed(1)}%)
                </div>
              );
            }
            return null;
          })()}

          <div className="grid grid-cols-2 gap-4" style={{ marginBottom: 16 }}>
            <Form.Item
              name="discount_percentage"
              label="Discount (%)"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                max={100}
                step={5}
                precision={2}
                className="w-full"
                suffix="%"
              />
            </Form.Item>

            <Form.Item
              name="discount_amount"
              label="Discount Amount"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                precision={2}
                className="w-full"
                prefix="$"
                disabled
              />
            </Form.Item>
          </div>

          {(() => {
            const qty = partForm.getFieldValue('quantity') || 0;
            const price = partForm.getFieldValue('unit_price') || 0;
            const discPct = partForm.getFieldValue('discount_percentage') || 0;
            const gross = qty * price;
            const discAmt = Math.round(price * discPct) / 100;
            const net = (price - discAmt) * qty;
            return (
              <div style={{ padding: 8, backgroundColor: '#f6ffed', borderRadius: 4, textAlign: 'center', marginTop: 8, marginBottom: 16 }}>
                {discPct > 0 ? (
                  <span>${price.toFixed(2)} × {qty} − {discPct}% (${(discAmt * qty).toFixed(2)}) = <strong>${net.toFixed(2)}</strong></span>
                ) : (
                  <span>${price.toFixed(2)} × {qty} = <strong>${gross.toFixed(2)}</strong></span>
                )}
              </div>
            );
          })()}

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true }]}
            style={{ marginBottom: 16 }}
          >
            <Select>
              <Option value="needed">Needed</Option>
              <Option value="ordered">Ordered</Option>
              <Option value="received">Received</Option>
              <Option value="installed">Installed</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="supplier"
            label="Supplier"
            style={{ marginBottom: 16 }}
          >
            <Input placeholder="Supplier name..." />
          </Form.Item>

          <Form.Item
            name="tracking_number"
            label="Tracking Number (Optional)"
            style={{ marginBottom: 16 }}
          >
            <Input placeholder="Tracking number..." />
          </Form.Item>

          <Form.Item
            name="expected_delivery"
            label="Expected Delivery (Optional)"
            style={{ marginBottom: 16 }}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
            style={{ marginBottom: 0 }}
          >
            <TextArea rows={2} placeholder="Additional notes..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Price Change on Approved Item Modal */}
      <Modal
        title="Price Changed on Approved Item"
        open={showPriceChangeModal}
        onCancel={() => { setShowPriceChangeModal(false); setLoading(false); }}
        onOk={handlePriceChangeConfirm}
        okText="Confirm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <strong>Item #{lineItem?.line_number}:</strong> {lineItem?.concern}
          </div>
          <div>Previously approved at: <strong>${priceChangeData?.oldTotal?.toFixed(2)}</strong></div>
          <div>
            New price: <strong>${priceChangeData?.newTotal?.toFixed(2)}</strong>
            <span style={{ color: (priceChangeData?.newTotal || 0) > (priceChangeData?.oldTotal || 0) ? '#ff4d4f' : '#52c41a', marginLeft: 4 }}>
              ({(priceChangeData?.newTotal || 0) > (priceChangeData?.oldTotal || 0) ? '+' : ''}
              ${((priceChangeData?.newTotal || 0) - (priceChangeData?.oldTotal || 0)).toFixed(2)})
            </span>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <Radio.Group value={priceChangeChoice} onChange={(e) => setPriceChangeChoice(e.target.value)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Radio value="resend">
                <div>
                  <strong>Re-send for customer approval</strong>
                  <div style={{ fontSize: 12, color: '#666' }}>Status resets to Pending. Customer gets new link.</div>
                </div>
              </Radio>
              <Radio value="in_person">
                <div>
                  <strong>Approve in-person</strong>
                  <div style={{ fontSize: 12, color: '#666' }}>Staff confirms customer agreed at new price.</div>
                </div>
              </Radio>
              <Radio value="proceed">
                <div>
                  <strong>Proceed without approval</strong>
                  <div style={{ fontSize: 12, color: '#faad14' }}>Warning: No customer consent recorded for price change.</div>
                </div>
              </Radio>
            </div>
          </Radio.Group>
        </div>
      </Modal>
    </Form>
  );
};

export default WorkOrderLineItemForm;
